import PropTypes from "prop-types"
import React, { useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import isEmail from "validator/lib/isEmail"

import api from "apis"
import { jewlr_t } from "helpers/application"
import { showGiftCardSale } from "helpers/holiday"

import withHistory from "./with-history"

const availableTimes = [
  "12:00 AM",
  "12:30 AM",
  "01:00 AM",
  "01:30 AM",
  "02:00 AM",
  "02:30 AM",
  "03:00 AM",
  "03:30 AM",
  "04:00 AM",
  "04:30 AM",
  "05:00 AM",
  "05:30 AM",
  "06:00 AM",
  "06:30 AM",
  "07:00 AM",
  "07:30 AM",
  "08:00 AM",
  "08:30 AM",
  "09:00 AM",
  "09:30 AM",
  "10:00 AM",
  "10:30 AM",
  "11:00 AM",
  "11:30 AM",
  "12:00 PM",
  "12:30 PM",
  "01:00 PM",
  "01:30 PM",
  "02:00 PM",
  "02:30 PM",
  "03:00 PM",
  "03:30 PM",
  "04:00 PM",
  "04:30 PM",
  "05:00 PM",
  "05:30 PM",
  "06:00 PM",
  "06:30 PM",
  "07:00 PM",
  "07:30 PM",
  "08:00 PM",
  "08:30 PM",
  "09:00 PM",
  "09:30 PM",
  "10:00 PM",
  "10:30 PM",
  "11:00 PM",
  "11:30 PM",
]

const availableTimezones = [
  ["America/New York", "America/New_York", "-05:00"],
  ["America/Chicago", "America/Chicago", "-06:00"],
  ["America/Denver", "America/Denver", "-07:00"],
  ["America/Los Angeles", "America/Los_Angeles", "-08:00"],
  ["Canada/Toronto", "America/Toronto", "-05:00"],
  ["Canada/Winnipeg", "America/Winnipeg", "-06:00"],
  ["Canada/Edmonton", "America/Edmonton", "-07:00"],
  ["Canada/Vancouver", "America/Vancouver", "-08:00"],
  ["Pacific/Honolulu", "Pacific/Honolulu", "-10:00"],
  ["Europe/London", "Europe/London", "+00:00"],
  ["Australia/Brisbane", "Australia/Brisbane", "+10:00"],
  ["Australia/Sydney", "Australia/Sydney", "+11:00"],
  ["Australia/Darwin", "Australia/Darwin", "+9:30"],
  ["Australia/Perth", "Australia/Perth", "+8:00"],
]

const formValidate = async (values) => {
  const errors = {}

  let userDate =
    new Date(Date.parse(values.email_send_date)).setHours(0, 0, 0, 0) + 86400000
  let validDate = new Date().setHours(0, 0, 0, 0)

  if (!values.gc.confirm_email) {
    errors["gc.confirm_email"] = "Please provide a confirmation email"
  } else if (values.gc.confirm_email !== values.gc.recipient_email) {
    errors["gc.confirm_email"] =
      "The Confirmation Email must match your Email Address"
  }

  if (!values.email_send_date) {
    errors["email_send_date"] = "Please provide a send date"
  } else if (validDate > userDate) {
    errors["email_send_date"] = "Please provide a present or future date"
  } else if (
    Date.parse(values.email_send_date) >
    new Date().getTime() + 31556952000
  ) {
    errors["email_send_date"] =
      "Please provide a date within a one year time frame"
  }

  if (values.gc.gift_message.length > 1024) {
    errors["gc.gift_message"] = "Exceeded maximum 1024 character length"
  }

  if (!values.gc.recipient_name) {
    errors["gc.recipient_name"] = "Please provide a recipient name"
  } else if (!/.{2,}/.test(values.gc.recipient_name)) {
    errors["gc.recipient_name"] = "Min. 2 characters"
  }

  if (!values.gc.recipient_email) {
    errors["gc.recipient_email"] = "Please provide a recipient email"
  } else if (!isEmail(values.gc.recipient_email)) {
    errors["gc.recipient_email"] = "Invalid email address"
  }

  let original_amount = values.gc.original_amount.replace(
    sl.config.default_currency,
    ""
  )
  if (original_amount < 50) {
    errors[
      "gc.original_amount"
    ] = `Please provide an amount of at least ${jewlr_t(
      "number.currency.format.unit"
    )}50`
  } else if (original_amount > 1000) {
    errors["gc.original_amount"] = `Please provide an amount between ${jewlr_t(
      "number.currency.format.unit"
    )}50 and ${jewlr_t("number.currency.format.unit")}1000`
  } else if (!/^\d+$/.test(original_amount)) {
    errors["gc.original_amount"] = "Please specify a valid number"
  }

  if (!values.gc.purchaser_name) {
    errors["gc.purchaser_name"] = "Please provide your name"
  } else if (!/.{2,}/.test(values.gc.purchaser_name)) {
    errors["gc.purchaser_name"] = "Min. 2 characters"
  }

  if (!values.gc.purchaser_email) {
    errors["gc.purchaser_email"] = "Please provide your email"
  } else if (!isEmail(values.gc.purchaser_email)) {
    errors["gc.purchaser_email"] = "Invalid email address"
  }

  return {
    errors,
    values,
  }
}

/**
 * Returns the browser time offset in correct format rounded up to the nearest half hour
 * @returns {string} - The formatted time offset ie. "05:00 PM"
 */
function getBrowserTime() {
  var date = new Date()
  // Time default input logic
  var coeff = 1000 * 60 * 30
  var rounded = new Date(Math.ceil(date.getTime() / coeff) * coeff)
  var timeFormatted = rounded.toLocaleString("en-US", {
    hour: "2-digit",
    hour12: true,
    minute: "2-digit",
  })

  return timeFormatted
}

/**
 * Returns the browser time zone offset in correct format
 * @returns {string} - The formatted time zone offset ie. "-05:00"
 */
function getBrowserTimeZoneOffset() {
  // Timezone default input logic
  // Convert the timezone offset from minutes to hours and change the sign since JavaScript offsets
  // are backward to the typical standard ie. -05:00 (Eastern US/Canada) is 5 in JavaScript.
  var date = new Date()
  var timezoneOffset = date.getTimezoneOffset() / 60
  var sign
  if (Math.sign(timezoneOffset) === 1) {
    sign = "-"
  } else {
    sign = "+"
  }
  var timezoneOffsetFormatted = `${sign}${
    Math.abs(timezoneOffset) < 10 ? "0" : ""
  }${Math.abs(timezoneOffset)}:00`

  let timezoneName = availableTimezones.filter((tz) => {
    return tz[2] == timezoneOffsetFormatted
  })
  if (timezoneName.length == 0) {
    timezoneName.push(availableTimezones[0])
  }
  return timezoneName[0][1]
}

export default function WithGiftCard(WrappedComponent) {
  const WithGiftCard = ({ amount, push, ...props }) => {
    const [browserTime, setBrowserTime] = useState(false)
    const [browserTimezoneOffset, setBrowserTimeZoneOffset] = useState(false)

    useEffect(() => {
      setBrowserTime(getBrowserTime())
      setBrowserTimeZoneOffset(getBrowserTimeZoneOffset())
    }, [])

    const {
      control,
      formState: { errors },
      handleSubmit,
      setError,
      setValue,
      watch,
    } = useForm({
      defaultValues: {
        dt: showGiftCardSale() ? "3kd3a3f" : "",
        email_send_date: new Date().toISOString().slice(0, 10),
        email_send_time: browserTime,
        email_send_timezone: browserTimezoneOffset,
        gc: {
          confirm_email: "",
          currency_code: jewlr_t("number.currency.code"),
          gift_message: "",
          merchant: "Jewlr",
          original_amount: amount || "",
          purchaser_email: "",
          purchaser_name: "",
          recipient_email: "",
          recipient_name: "",
          site_url: `https://${sl.config.site_url}`,
        },
        show_timezone_form: "no",
      },
      mode: "onTouched",
      resolver: formValidate,
    })

    const onSubmit = async (values) => {
      const url = "/secure/api/v10/gift_card"
      const original_amount = values.gc.original_amount.replace(
        sl.config.default_currency,
        ""
      )
      try {
        const response = await api.post(url, {
          ...values,
          gc: { ...values.gc, original_amount: original_amount },
        })
        if (response.data) {
          push(`/payments/${response.data.payment_token}`)
        }
      } catch (err) {
        console.error(err)
        setError("root.error", {
          message: err?.errors?.[0] || err?.message || err,
        })
      }
    }

    return (
      <form onSubmit={handleSubmit(onSubmit)}>
        <WrappedComponent
          availableTimes={availableTimes}
          availableTimezones={availableTimezones}
          control={control}
          errors={errors}
          setValue={setValue}
          watch={watch}
          {...props}
        />
      </form>
    )
  }

  WithGiftCard.propTypes = {
    amount: PropTypes.string,
    push: PropTypes.func.isRequired,
  }

  return withHistory(WithGiftCard)
}
